import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Input, Modal } from "semantic-ui-react";

import { Can } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { useCommunicationTemplatesTableHandlers } from "modules/CommunicationTemplates/components/CommunicationTemplatesTable/hooks";

import type { CommunicationMedium } from "models";
import type { DuplicateMediumFormValues } from "modules/CommunicationTemplates/components";

type DuplicateMediumModalFormProps = {
  hasErrors: boolean;
  isDuplicateMediumPending: boolean;
  medium: CommunicationMedium;
  onClose: () => void;
  onSubmit: (fields: DuplicateMediumFormValues) => void;
};

const FormHelper = createFormHelper<DuplicateMediumFormValues>();

export const DuplicateMediumModalForm = ({ hasErrors, onClose, onSubmit, medium, isDuplicateMediumPending }: DuplicateMediumModalFormProps) => {
  const { t } = useTranslation();

  const { getCommunicationType, getCommunicationChannel } = useCommunicationTemplatesTableHandlers();
  const { watch, handleSubmit } = FormHelper.useFormContext();
  const [carBrand, carModel] = watch(["car_brand", "car_model"]);

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <span className="header-title"> {t("v8_duplicate_template", "Duplicate Template")} </span>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        <Grid columns="equal">
          <GridColumn>
            <Form.Field>
              <label>{t("v8_communication_type", "Communication Type")} </label>
              <Input
                placeholder={t("v8_communication_type", "Communication Type")}
                fluid
                value={getCommunicationType({
                  acses_communication_event_type: medium.acses_communication_event_type,
                  event_type: medium.event_type,
                  keylocker_communication_event_type: medium.keylocker_communication_event_type
                })}
                disabled
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field>
              <label>{t("v8_channel", "Channel")} </label>
              <Input placeholder={t("v8_channel", "Channel")} fluid value={getCommunicationChannel(medium.channel)} disabled />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!carBrand && hasErrors}>
              <label>{t("v8_brand", "Brand")} </label>
              <FormHelper.Field.Input name="car_brand" inputProps={{ placeholder: t("v8_brand", "Brand"), fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field error={!carModel && hasErrors}>
              <label>{t("v8_model", "Model")} </label>
              <FormHelper.Field.Input name="car_model" inputProps={{ placeholder: t("v8_model", "Model"), fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="save-cancel-buttons-container">
          <Form.Button className="light" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Form.Button>
          <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.Mediums}>
            <Form.Button type="submit" color="green" disabled={isDuplicateMediumPending} loading={isDuplicateMediumPending} onClick={handleSubmit(d => onSubmit(d))}>
              {t("v8_create", "Create")}
              <Icon className="check" />
            </Form.Button>
          </Can>
        </div>
      </Modal.Actions>
    </>
  );
};
